/**
 * Extension auth: after user is logged in, redirect to Chrome extension redirect URL
 * with access_token and user_id so the extension can store them.
 * Used by the wlkthru Chrome extension "Log in with wlkthru" flow.
 */

import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";

export default function ExtensionAuthPage() {
  const [status, setStatus] = useState<"loading" | "redirecting" | "need-login" | "error">("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirectUrl = params.get("redirect");
    if (!redirectUrl) {
      setStatus("error");
      setMessage("Missing redirect URL. Open this page from the wlkthru extension.");
      return;
    }
    // Only allow redirecting to chrome-extension protocol or Chromium app redirect host
    const allowed =
      redirectUrl.startsWith("https://") &&
      (redirectUrl.includes(".chromiumapp.org/") || redirectUrl.startsWith("chrome-extension://"));
    if (!allowed) {
      setStatus("error");
      setMessage("Invalid redirect URL.");
      return;
    }

    (async () => {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();
      if (error) {
        setStatus("error");
        setMessage("Could not get session.");
        return;
      }
      if (!session?.access_token || !session?.user?.id) {
        setStatus("need-login");
        return;
      }
      setStatus("redirecting");
      const sep = redirectUrl.includes("?") ? "&" : "?";
      const target = `${redirectUrl}${sep}access_token=${encodeURIComponent(session.access_token)}&user_id=${encodeURIComponent(session.user.id)}`;
      window.location.href = target;
    })();
  }, []);

  if (status === "loading" || status === "redirecting") {
    return (
      <main className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">
          {status === "loading" ? "Checking login…" : "Redirecting to extension…"}
        </p>
      </main>
    );
  }

  if (status === "need-login") {
    return (
      <main className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <h1 className="text-lg font-semibold mb-2">Log in first</h1>
          <p className="text-muted-foreground text-sm mb-4">
            You need to be logged in to wlkthru to connect the extension. Log in below, then try again from the extension.
          </p>
          <Link
            to="/auth"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Log in to wlkthru
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="text-center max-w-sm">
        <p className="text-destructive text-sm">{message}</p>
        <Link to="/dashboard" className="text-primary text-sm mt-2 inline-block">
          Go to dashboard
        </Link>
      </div>
    </main>
  );
}
